import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAuthStore } from '../../store';
import TopNav from '../../components/layout/TopNav';
import FormError from '../../components/shared/FormError';
import ServerError from '../../components/shared/ServerError';
import { UserRole } from '../../types';

const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'One number', test: (p: string) => /\d/.test(p) },
  { label: 'One special character', test: (p: string) => /[!@#$%^&*()_+\-=[\]{}|;:',.<>?/]/.test(p) },
];

const ProfilePage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const userRole = useAuthStore((s) => s.user?.role);
  const homeLink = userRole === UserRole.GROUP_ADMIN ? '/group-admin' : '/dashboard';

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const ruleResults = useMemo(
    () => PASSWORD_RULES.map((rule) => ({ ...rule, passed: rule.test(newPassword) })),
    [newPassword],
  );

  const allRulesPassed = ruleResults.every((r) => r.passed);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = allRulesPassed && passwordsMatch && currentPassword.length > 0 && !loading;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!allRulesPassed) {
      setError('Password does not meet all requirements.');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from the current password.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        email: user?.email,
        currentPassword,
        newPassword,
      });

      toast.success('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: Array<{ message?: string }> } } })
        ?.response?.data?.errors?.[0]?.message;
      setError(msg ?? 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-white transition-colors duration-300 font-sans flex flex-col relative overflow-hidden">
      <TopNav />

      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-10 relative z-10 flex flex-col gap-6">
        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
          <Link to={homeLink} className="text-slate-400 hover:text-primary transition-colors uppercase">
            Home
          </Link>
          <span className="text-slate-300">/</span>
          <span className="text-primary uppercase">Settings</span>
        </div>

        {/* Page Header */}
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Account Settings</h2>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Manage your profile, security, and integrations</p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          {/* Change Password Card */}
          <div className="h-full border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm bg-white dark:bg-slate-900 overflow-hidden">
            <div className="p-8 pb-4">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <span className="material-symbols-outlined text-primary text-[24px]">shield</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white">Change Password</h3>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mt-1">Account Security Settings</p>
                </div>
              </div>
            </div>

            <div className="p-8 pt-4">
              <ServerError message={error} className="mb-6" />

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Current Password */}
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                    Current Password <span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrent ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="rounded-xl h-14 w-full bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800 transition-all focus:bg-white dark:focus:bg-slate-900 focus:border-primary focus:ring-4 focus:ring-primary/5 px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 outline-none"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrent(!showCurrent)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors outline-none"
                    >
                      <span className="material-symbols-outlined text-[20px]">{showCurrent ? 'visibility_off' : 'visibility'}</span>
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                    New Password <span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showNew ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="rounded-xl h-14 w-full bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800 transition-all focus:bg-white dark:focus:bg-slate-900 focus:border-primary focus:ring-4 focus:ring-primary/5 px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 outline-none"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew(!showNew)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors outline-none"
                    >
                      <span className="material-symbols-outlined text-[20px]">{showNew ? 'visibility_off' : 'visibility'}</span>
                    </button>
                  </div>

                  {/* Password Strength Checklist */}
                  {newPassword.length > 0 && (
                    <div className="bg-slate-50 dark:bg-slate-950 p-5 rounded-xl space-y-3 border border-slate-100 dark:border-slate-800">
                      <span className="block text-[10px] font-bold text-slate-400 tracking-[0.2em] uppercase mb-2">
                        Requirements
                      </span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {ruleResults.map((rule, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <div className={`p-1 rounded-full ${rule.passed ? 'bg-green-100 text-green-600' : 'bg-slate-200 dark:bg-slate-700 text-slate-400'}`}>
                              {rule.passed ? (
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                              ) : (
                                <div className="h-3 w-3" />
                              )}
                            </div>
                            <span className={`text-[10px] font-bold tracking-wide uppercase ${rule.passed ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'}`}>
                              {rule.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                    Confirm New Password <span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`rounded-xl h-14 w-full border transition-all focus:ring-4 px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 outline-none ${
                        confirmPassword.length > 0 && !passwordsMatch
                          ? 'bg-red-50/50 dark:bg-red-950/20 border-red-300 dark:border-red-900 focus:border-red-400 focus:ring-red-500/5'
                          : 'bg-slate-50 dark:bg-slate-950 border-slate-100 dark:border-slate-800 focus:bg-white dark:focus:bg-slate-900 focus:border-primary focus:ring-primary/5'
                      }`}
                      aria-invalid={confirmPassword.length > 0 && !passwordsMatch}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors outline-none"
                    >
                      <span className="material-symbols-outlined text-[20px]">{showConfirm ? 'visibility_off' : 'visibility'}</span>
                    </button>
                  </div>
                  <FormError message={confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match' : undefined} />
                  {passwordsMatch && (
                    <p className="mt-1.5 ml-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-green-600">
                      <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                      Passwords match
                    </p>
                  )}
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="w-full sm:w-auto h-14 rounded-xl px-10 text-sm font-bold transition-all active:scale-95 bg-primary text-on-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Updating...
                      </span>
                    ) : (
                      'Update Password'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Security Info Card */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-12 flex flex-col justify-center text-center space-y-6">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-white dark:bg-slate-800 shadow-xl shadow-primary/5">
              <span className="material-symbols-outlined text-primary text-[40px]">shield</span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">Security Best Practices</h3>
              <p className="mt-4 text-sm text-slate-500 dark:text-slate-400 font-medium leading-relaxed max-w-sm mx-auto">
                To keep your account secure, use a strong password and update it regularly. Never share your credentials with anyone.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
